import styled, { css } from 'styled-components';
import { Cascader } from 'antd';
import { themeColor } from '~/common/utils/theme';
import { Typography } from '~/ui/components';

export type CustomComponentsAreaProps = {
  newWidth?: string | number;
};

export const PortalSelect = styled.div<CustomComponentsAreaProps>`
  .ant-cascader-menus {
    min-width: ${({ newWidth }) => (Number.isInteger(newWidth) && newWidth > 0 ? `${newWidth}px` : 'auto')};
    div {
      display: flex;
      position: relative;
      align-items: center;
    }
  }
  .ant-cascader-menu:first-child {
    width: 100%;
    min-width: 300px;
  }
  .ant-cascader-menus.left div {
    flex-direction: row-reverse;
  }
`;

export const StyledCascader = styled(Cascader)`
  width: 100%;
  height: 56px;
  border: 1px solid ${({ theme }) => theme.colors.divider.primary};
  border-radius: 8px;
  &:focus,
  &:focus-within,
  &.ant-cascader-picker-focused {
    border-color: ${themeColor('brand.primary')};
  }

  &:hover {
    ${({ isDisabled }) =>
      isDisabled
        ? css`
            background-color: ${themeColor('element.disabledBackground')};
          `
        : css`
            background-color: ${themeColor('background.secondary', {
              dark: 'background.primary',
            })} !important;
          `}
  }

  &.ant-cascader-picker {
    border-radius: 8px;
    background-color: ${themeColor('background.primary')};

    .ant-cascader-picker-label {
      font-size: ${({ theme }) => theme.typography.types.bodySmall.size};
      line-height: ${({ theme }) => theme.typography.types.bodySmall.lineHeight};
      font-family: ${({ theme }) => theme.typography.fonts.libreFranklin};
      color: ${themeColor('element.primary', { dark: 'element.placeholder' })};
      padding: 0 11px;
    }

    .ant-cascader-input {
      border: none;
      outline: none !important;
      box-shadow: none !important;
      font-size: ${({ theme }) => theme.typography.types.bodySmall.size};
      line-height: ${({ theme }) => theme.typography.types.bodySmall.lineHeight};
      font-family: ${({ theme }) => theme.typography.fonts.libreFranklin};
      caret-color: transparent;
      &:focus,
      &:active {
        outline: none;
        box-shadow: none;
      }
    }

    .ant-input-lg {
      height: 56px;
      padding: 0 8px;
    }

    &.ant-cascader-picker-disabled {
      opacity: 0.6;
    }

    &.ant-cascader-picker-with-value {
      .ant-cascader-picker-label {
        color: transparent;
      }
    }

    &.ant-cascader-picker-with-value,
    &.ant-cascader-picker-focused {
      .ant-input-lg {
        height: 24px;
        transform: translate(0, 25px);
      }
    }

    &:has(.ant-cascader-picker-label:not(:empty)) {
      .ant-input-lg {
        height: 24px;
        transform: translate(0, 25px);
      }

      .ant-cascader-picker-label {
        transform: translate(0, 9px);
      }
    }
  }

  .ant-cascader-picker-arrow {
    color: ${themeColor('element.secondary', { dark: 'element.secondary' })};
  }

  .ant-cascader-picker-clear {
    background-color: ${themeColor('background.secondary', { dark: 'background.primary' })};
    color: ${themeColor('element.secondary', { dark: 'element.secondary' })};
  }
`;

export const SelectLabel = styled(Typography).attrs({
  element: 'label',
  type: 'bodyMedium',
  color: 'element.placeholder',
})`
  position: absolute;
  transform: translate(8px, 16px);
  pointer-events: none;
  z-index: 8;
  transition-property: transform, font-size, color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  &:has(+ .ant-cascader-picker-with-value),
  &:has(+ .ant-cascader-picker-focused),
  &:has(+ .ant-cascader-picker .ant-cascader-picker-label:not(:empty)) {
    font-size: ${({ theme }) => theme.typography.types.bodyXSmall.size};
    font-weight: ${({ theme }) => theme.typography.types.bodyXSmall.weight};
    line-height: ${({ theme }) => theme.typography.types.bodyXSmall.lineHeight};
    color: ${({ theme }) => theme.colors.element.secondary};
    transform: translate(12px, 8px);
  }
`;
